<script lang="ts" setup>
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { useLeaderboardPodiumData } from '#leaderboards/composables/useLeaderboardPodiumData'
import type { ContributionPoint, LoanUtilizationPoint } from '#public-research/utils/mm-monitoring.data'
import { resolveMmLogo } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { useQuery } from '@pinia/colada'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@forgd/ui-kit/table'
import { ToggleGroup, ToggleGroupItem } from '@forgd/ui-kit/toggle-group'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { FORGD_SYSTEM_PROJECT_ID } from '@forgd/shared/constants'

const auth = useAuth()
const mmmPath = computed(() => {
  const project = auth.project
  const projectId = auth.isLoggedIn && project?.tokenListed ? project.id : FORGD_SYSTEM_PROJECT_ID
  return `/project/${projectId}/market-maker-monitoring/dashboard`
})

const segColors = [chartColors.tokenDesigner[4]!, chartColors.tokenDesigner[5]!]

const contribMode = ref<'depth' | 'volume'>('depth')
const contribOptions = [
  { value: 'depth', label: 'Depth' },
  { value: 'volume', label: 'Volume' },
] as const
const contribTitle = computed(() => `${contribMode.value === 'depth' ? 'Depth' : 'Volume'} ($) | Date`)

const tradeVolumeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/mm-monitoring/trade-volume-by-engagement-type'),
  refetchOnWindowFocus: false,
})
const tradeVolume = computed(() => tradeVolumeQuery.data.value ?? [])
const tradeVolumeLoading = tradeVolumeQuery.isLoading

const contributionsQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/mm-monitoring/contributions-timeseries', {
    params: () => ({ query: { metric: contribMode.value } }),
  }),
  refetchOnWindowFocus: false,
})
const contributions = computed<ContributionPoint[]>(() =>
  (contributionsQuery.data.value ?? []).map((row, index) => ({
    index,
    month: formatValue(row.date, 'date:month'),
    loanCallOption: row.byType.find(b => b.engagementType === 'Loan + Call Option')?.value ?? 0,
    retainerWorkingCapital: row.byType.find(b => b.engagementType === 'Retainer + Working Capital')?.value ?? 0,
  })),
)
const contribTicks = computed(() => contributions.value.map((_, i) => i))
// Its own query drives loading so the chart shows a loading state while it
// refetches on the depth/volume toggle, not just on first mount.
const contributionsLoading = contributionsQuery.isLoading

const engagementsQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/mm-monitoring/engagements-summary'),
  refetchOnWindowFocus: false,
})
// The two public engagement types are fixed, so map live counts onto them: the
// card keeps both labelled rows while the values load.
const engagementTypes = ['Loan + Call Option', 'Retainer + Working Capital'] as const
const engagements = computed(() => {
  const byType = new Map((engagementsQuery.data.value?.byType ?? []).map(e => [e.engagementType, e.count]))
  return {
    total: engagementsQuery.data.value?.total ?? 0,
    segments: engagementTypes.map(label => ({ label, value: byType.get(label) ?? 0 })),
  }
})
const engagementsLoading = engagementsQuery.isLoading
const pct = (value: number) => (engagements.value.total ? (value / engagements.value.total) * 100 : 0)

const spreadsQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/mm-monitoring/spreads-by-fdv-tier'),
  refetchOnWindowFocus: false,
})
const spreadsByFdvTier = computed(() =>
  (spreadsQuery.data.value ?? []).map(row => ({ label: `${row.tier} FDV`, spread: row.avgSpreadPercent })),
)
const spreadsLoading = spreadsQuery.isLoading

const loanUtilizationQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/mm-monitoring/loan-utilization'),
  refetchOnWindowFocus: false,
})
// Join depth and volume by date (the contract doesn't guarantee equal length or
// ordering) and keep null utilization as a gap rather than a fabricated 0%.
const loanUtilizationDates = computed(() =>
  [...new Set([
    ...(loanUtilizationQuery.data.value?.depth ?? []).map(p => p.date),
    ...(loanUtilizationQuery.data.value?.volume ?? []).map(p => p.date),
  ])].sort(),
)
const loanUtilization = computed<LoanUtilizationPoint[]>(() => {
  const depthByDate = new Map((loanUtilizationQuery.data.value?.depth ?? []).map(p => [p.date, p.utilizationPercent]))
  const volumeByDate = new Map((loanUtilizationQuery.data.value?.volume ?? []).map(p => [p.date, p.utilizationPercent]))
  return loanUtilizationDates.value.map((date, index) => ({
    index,
    depthPct: depthByDate.get(date) ?? null,
    volumePct: volumeByDate.get(date) ?? null,
  }))
})
const loanUtilizationAxis = computed(() => {
  const dates = loanUtilizationDates.value
  return {
    start: dates.length ? formatValue(dates[0]!, 'date:day') : '',
    end: dates.length ? formatValue(dates[dates.length - 1]!, 'date:day') : '',
  }
})
const loanUtilizationLoading = loanUtilizationQuery.isLoading

// Podium depth is the Leading Depth card's data source and drives only its loading gate.
const { aggregatedById, isLoading: loading } = useLeaderboardPodiumData()
const leadingDepth = computed(() =>
  [...aggregatedById.value.values()]
    .filter(mm => mm.bidAskDepth.value != null)
    .sort((a, b) => (b.bidAskDepth.value ?? 0) - (a.bidAskDepth.value ?? 0))
    .slice(0, 3)
    .map(mm => ({
      name: mm.marketMakerName,
      logo: mm.marketMakerImageUrl,
      depth: mm.bidAskDepth.value!,
      share: mm.bidAskDepthMarketPercentage.value,
    })),
)
</script>

<template>
  <section class="@container flex flex-col gap-4">
    <header class="flex flex-col gap-1">
      <h2 class="text-sm font-semibold text-primal">
        Market Maker Monitoring
      </h2>
      <p class="text-xs text-secondary">
        Historical average performance of tracked engagements
      </p>
    </header>

    <div class="grid grid-cols-1 items-stretch gap-4 @3xl:grid-cols-2 @6xl:grid-cols-[416px_minmax(0,1fr)_minmax(0,1fr)]">
      <Card class="flex h-full flex-col @3xl:col-span-2 @6xl:col-span-1">
        <CardHeader class="flex min-h-14 flex-row items-center border-b border-default py-3">
          <CardTitle class="flex items-center gap-1.5">
            <span>Total Engagements Tracked:</span>
            <Skeleton v-if="engagementsLoading" class="h-4 w-12" />
            <span v-else>{{ formatValue(engagements.total, 'number:0') }}</span>
          </CardTitle>
        </CardHeader>
        <div class="flex flex-1 flex-col divide-y divide-default">
          <div class="grid grid-cols-2 divide-x divide-default">
            <div
              v-for="(seg, i) in engagements.segments"
              :key="seg.label"
              class="flex flex-col px-4 py-3"
            >
              <div class="flex items-center gap-2">
                <div
                  class="min-h-full w-1 shrink-0 rounded-full bg-secondary-subtle"
                  :style="`background-color: ${segColors[i]};`"
                />
                <div class="flex flex-col gap-2">
                  <div class="text-xs text-secondary">
                    {{ seg.label }}
                  </div>
                  <Skeleton v-if="engagementsLoading" class="h-4 w-10" />
                  <div v-else class="text-sm leading-none font-semibold text-primal">
                    {{ formatValue(seg.value, 'number:0') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mt-auto p-4">
            <template v-if="engagementsLoading">
              <Skeleton class="h-2 w-full rounded-full" />
              <div class="mt-2 flex justify-between">
                <Skeleton class="h-3 w-10" />
                <Skeleton class="h-3 w-10" />
              </div>
            </template>
            <template v-else>
              <div class="flex h-2 overflow-hidden rounded-full">
                <div
                  v-for="(seg, i) in engagements.segments"
                  :key="seg.label"
                  :style="{ width: `${pct(seg.value)}%`, backgroundColor: segColors[i] }"
                />
              </div>
              <div class="mt-2 flex justify-between font-mono text-xs font-medium">
                <span :style="`color: ${segColors[0]};`">{{ formatValue(pct(engagements.segments[0]!.value), 'percent:2') }}</span>
                <span :style="`color: ${segColors[1]};`">{{ formatValue(pct(engagements.segments[1]!.value), 'percent:2') }}</span>
              </div>
            </template>
          </div>
        </div>
      </Card>

      <Card class="flex h-full flex-col">
        <CardHeader class="flex min-h-14 flex-row items-center justify-between border-b border-default py-3 pr-3">
          <CardTitle>Avg. MM Spreads by FDV Tier</CardTitle>
          <Button as-child variant="outline" size="sm" icon-only aria-label="View spreads by FDV tier">
            <NuxtLink :to="`${mmmPath}#spreads`">
              <ArrowRightIcon aria-hidden="true" />
            </NuxtLink>
          </Button>
        </CardHeader>
        <div class="flex flex-1 flex-col p-4">
          <div class="mt-auto overflow-hidden rounded-md border border-default">
            <Table>
              <TableBody class="divide-y divide-default">
                <template v-if="spreadsLoading">
                  <TableRow v-for="n in 3" :key="`spread-skeleton-${n}`" class="h-8">
                    <TableCell class="px-3 py-1">
                      <Skeleton class="h-4 w-16" />
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right">
                      <Skeleton class="ml-auto h-4 w-14" />
                    </TableCell>
                  </TableRow>
                </template>
                <template v-else>
                  <TableRow v-for="tier in spreadsByFdvTier" :key="tier.label" class="h-8">
                    <TableCell class="px-3 py-1 text-secondary">
                      {{ tier.label }}
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right">
                      <span class="font-mono font-medium text-primal">{{ formatValue(tier.spread, 'percent:2') }}</span>
                    </TableCell>
                  </TableRow>
                </template>
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>

      <Card class="flex h-full flex-col">
        <CardHeader class="flex min-h-14 flex-row items-center justify-between border-b border-default py-3 pr-3">
          <CardTitle>Leading Depth by Market Maker (+/- 2.00%)</CardTitle>
          <Button as-child variant="outline" size="sm" icon-only aria-label="View leading depth by market maker">
            <NuxtLink :to="`${mmmPath}#depth`">
              <ArrowRightIcon aria-hidden="true" />
            </NuxtLink>
          </Button>
        </CardHeader>
        <div class="flex flex-1 flex-col p-4">
          <div class="mt-auto overflow-hidden rounded-md border border-default">
            <Table>
              <TableBody class="divide-y divide-default">
                <template v-if="loading">
                  <TableRow v-for="n in 3" :key="n" class="h-8">
                    <TableCell class="px-3 py-1">
                      <span class="flex items-center gap-2">
                        <Skeleton class="size-5 shrink-0 rounded-full" />
                        <Skeleton class="h-4 w-24" />
                      </span>
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right">
                      <Skeleton class="ml-auto h-4 w-16" />
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right">
                      <Skeleton class="ml-auto h-4 w-16" />
                    </TableCell>
                  </TableRow>
                </template>
                <template v-else>
                  <TableRow v-for="mm in leadingDepth" :key="mm.name" class="h-8">
                    <TableCell class="px-3 py-1">
                      <span class="flex items-center gap-2">
                        <img :src="resolveMmLogo(mm.logo)" :alt="mm.name" class="size-5 shrink-0 rounded-full object-cover">
                        <span class="text-primal">{{ mm.name }}</span>
                      </span>
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right">
                      <span class="font-mono font-medium text-primal">${{ formatValue(mm.depth, 'number:0') }}</span>
                    </TableCell>
                    <TableCell class="px-3 py-1 text-right text-secondary">
                      {{ formatValue(mm.share, 'percent:2') }} of Market
                    </TableCell>
                  </TableRow>
                  <TableRow v-if="!leadingDepth.length" class="h-8">
                    <TableCell colspan="3" class="px-3 py-1 text-secondary">
                      No market maker data available
                    </TableCell>
                  </TableRow>
                </template>
              </TableBody>
            </Table>
          </div>
        </div>
      </Card>
    </div>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-[416px_minmax(0,1fr)]">
      <div class="flex flex-col gap-4">
        <Card class="flex flex-col">
          <CardHeader class="flex min-h-14 flex-row items-center justify-start gap-2 border-b border-default py-3">
            <CardTitle>Avg. Market Maker Trade Vol.</CardTitle>
            <Badge variant="gray">
              30D
            </Badge>
          </CardHeader>
          <div class="p-4">
            <div class="overflow-hidden rounded-md border border-default">
              <Table>
                <TableHeader>
                  <TableRow class="h-8">
                    <TableHead class="px-3">
                      Engagement Type
                    </TableHead>
                    <TableHead class="px-3 text-right">
                      Maker
                    </TableHead>
                    <TableHead class="px-3 text-right">
                      Taker
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody class="divide-y divide-default">
                  <template v-if="tradeVolumeLoading">
                    <TableRow v-for="i in 2" :key="`tv-skeleton-${i}`" class="h-8">
                      <TableCell class="px-3">
                        <Skeleton class="h-4 w-32" />
                      </TableCell>
                      <TableCell class="px-3 text-right">
                        <Skeleton class="ml-auto h-4 w-14" />
                      </TableCell>
                      <TableCell class="px-3 text-right">
                        <Skeleton class="ml-auto h-4 w-14" />
                      </TableCell>
                    </TableRow>
                  </template>
                  <template v-else>
                    <TableRow v-for="row in tradeVolume" :key="row.engagementType" class="h-8">
                      <TableCell class="px-3 text-xs text-secondary">
                        {{ row.engagementType }}
                      </TableCell>
                      <TableCell class="px-3 text-right text-xs">
                        <span class="font-mono font-medium text-primal">{{ formatValue(row.avgMakerVolume, 'currency:short') }}</span>
                      </TableCell>
                      <TableCell class="px-3 text-right text-xs">
                        <span class="font-mono font-medium text-primal">{{ formatValue(row.avgTakerVolume, 'currency:short') }}</span>
                      </TableCell>
                    </TableRow>
                  </template>
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>

        <Card class="flex flex-1 flex-col">
          <CardHeader class="flex min-h-14 flex-row items-center justify-start gap-2 border-b border-default py-3">
            <CardTitle>Avg. Loan Utilization</CardTitle>
            <Badge variant="gray">
              30D
            </Badge>
          </CardHeader>
          <div class="flex flex-1 flex-col px-4 pt-2 pb-4">
            <PrMmLoanUtilizationChart
              :data="loanUtilization"
              :start-label="loanUtilizationAxis.start"
              :end-label="loanUtilizationAxis.end"
              :loading="loanUtilizationLoading"
            />
          </div>
        </Card>
      </div>

      <Card class="flex flex-col">
        <CardHeader class="flex min-h-14 flex-col items-stretch gap-2 border-b border-default py-3 md:flex-row md:items-center md:justify-between md:pr-3">
          <div class="flex items-center justify-between gap-2 md:justify-start">
            <CardTitle class="whitespace-normal min-[360px]:whitespace-nowrap md:whitespace-normal">
              Market Maker’s Contributions to Trade Activity
            </CardTitle>
            <Badge variant="gray">
              30D
            </Badge>
          </div>
          <ToggleGroup v-model="contribMode" preset="light" layout="fit" size="sm" class="w-full md:w-auto">
            <ToggleGroupItem v-for="opt in contribOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </ToggleGroupItem>
          </ToggleGroup>
        </CardHeader>
        <div class="flex flex-1 flex-col px-5 pt-3 pb-5">
          <PrMmContributionsChart
            :data="contributions"
            :ticks="contribTicks"
            :title="contribTitle"
            :loading="contributionsLoading"
          />
        </div>
      </Card>
    </div>
  </section>
</template>
